import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  type GridColDef,
  type GridPaginationModel,
  type GridRowSelectionModel,
} from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataGridNumberedPagination, DATA_GRID_DEFAULT_PAGE_SIZE, DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS } from '../../components/DataGridNumberedPagination';
import { ChemInline } from '../../components/ChemInline';
import { shouldRenderAsChemLatex } from '../../chemistry/latexChemDisplay';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import {
  customerRelationWrite,
  normalizeDynamicRow,
  strapiCreateDynamicEntity,
  strapiDeleteDynamicEntity,
  strapiListDynamicEntityPage,
  strapiUpdateDynamicEntity,
  userRelationWrite,
} from '../../api/strapiDynamicEntity';
import { queryKeys } from '../../query/queryKeys';
import { relationFieldToRows } from '../../api/jenisPengukuranPerlengkapan';
import {
  buildPeralatanToParameterUjiWritePayload,
  draftFromPeralatanToParameterUjiRow,
  emptyPeralatanToParameterUjiDraft,
  strapiFindPeralatanToParameterUjiDocumentId,
  type PeralatanToParameterUjiDraft,
} from '../../api/peralatanToParameterUji';
import type { StrapiManagedUserRow } from '../../api/strapiUsers';
import { useAuth } from '../../auth/AuthContext';
import { appDocumentTitle } from '../../appMeta';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { PageShell } from '../../layout/PageShell';
import type { MasterCrudRegistryEntry, MasterFormField, MasterGridColumn } from '../../masterData/masterCrudRegistry';
import {
  createReferensiMasterGroupEntry,
  kodeKategoriFromReferensiSlug,
  labelFromReferensiKodeKategori,
  MASTER_CRUD_REGISTRY,
} from '../../masterData/masterCrudRegistry';
import { FormQuestionGroupEditor } from './FormQuestionGroupEditor';
import { PeralatanToParameterUjiEditor } from './PeralatanToParameterUjiEditor';
import { FormQuestionGroupedCheckboxField } from './FormQuestionGroupedCheckboxField';
import { FormQuestionMapJenisBar } from './FormQuestionMapJenisBar';
import { FormQuestionOptionMapQuestionsBar } from './FormQuestionOptionMapQuestionsBar';
import {
  documentIdsInFormQuestionGroups,
  draftFromFormQuestionGroup,
  emptyFormQuestionGroupDraft,
  FORM_QUESTION_LIST_FETCH_SIZE,
  formQuestionGroupKey,
  formQuestionNameTaken,
  groupFormQuestionsByName,
  paginateFormQuestionGroups,
  planFormQuestionGroupSave,
  questionsForFormName,
  type FormQuestionGroup,
  type FormQuestionGroupDraft,
} from './formQuestionGroups';
import { uniqueFormQuestionNames, rowSelectionDocumentIds } from './formQuestionMapJenis';
import { applyFormQuestionOptionRangeWrite } from '../../api/formSampling';
import {
  useCustomersQuery,
  useMasterEntityCreateMutation,
  useMasterEntityDeleteMutation,
  useMasterEntityListQuery,
  useMasterEntityMiniListQuery,
  useMasterEntityUpdateMutation,
  useQuotationsQuery,
  useSampleInformationsListQuery,
  useTestResultsListQuery,
  useUserTypesQuery,
  useUsersQuery,
} from '../../query/hooks';

function relDoc(rel: unknown): string {
  if (!rel || typeof rel !== 'object') return '';
  const o = rel as Record<string, unknown>;
  return String(o.documentId ?? '');
}

function connectPayload(documentIdTrimmed: string | undefined): { connect: string[] } | undefined {
  const t = documentIdTrimmed?.trim();
  if (!t) return undefined;
  return { connect: [t] };
}

function groupingOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const name = String(row.nama_grouping ?? '');
  return name || String(row.documentId ?? '');
}

function jenisPengukuranOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const nama = String(row.nama_jenis ?? '');
  const kode = String(row.kode_jenis ?? '');
  return [nama, kode].filter(Boolean).join(' — ') || String(row.documentId ?? '');
}

function stringifyJenisPengukuranRel(rel: unknown): string {
  return relationFieldToRows(rel)
    .map((row) => jenisPengukuranOptionLabel(row))
    .filter(Boolean)
    .join(', ');
}

function uniqueJenisPengukuranLabels(questions: DynamicEntityRow[]): string[] {
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const question of questions) {
    for (const row of relationFieldToRows(question.jenis_pengukuran)) {
      const label = jenisPengukuranOptionLabel(row);
      if (!label || seen.has(label)) continue;
      seen.add(label);
      labels.push(label);
    }
  }
  return labels;
}

function formQuestionMasterLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const r = row as Record<string, unknown>;
  const name = String(r.form_question_name ?? '').trim();
  const title = String(r.title ?? '').trim();
  const jenis = stringifyJenisPengukuranRel(r.jenis_pengukuran);
  return [name, title, jenis].filter(Boolean).join(' · ') || String(r.documentId ?? '');
}

function bakuMutuOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const k = String(row.kode ?? '');
  const n = String(row.nama_baku_mutu ?? '');
  return [k, n].filter(Boolean).join(' — ') || String(row.documentId ?? '');
}

function parameterUjiOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const n = String(row.nama_parameter ?? '');
  const s = String(row.simbol ?? '');
  const bits = [n, s].filter(Boolean);
  return bits.length ? bits.join(' ') : String(row.documentId ?? '');
}

function sniMethodOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const kode = String(row.kode_sni ?? '').trim();
  const judul = String(row.judul ?? '').trim();
  const short = judul.length > 80 ? `${judul.slice(0, 80)}…` : judul;
  return [kode, short].filter(Boolean).join(' — ') || String(row.documentId ?? '');
}

function referensiOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const kk = String(row.kode_kategori ?? '');
  const k = String(row.kode ?? '');
  const d = String(row.deskripsi1 ?? '').slice(0, 48);
  const bits = [kk, k, d].filter(Boolean);
  return bits.length ? bits.join(' · ') : String(row.documentId ?? '');
}

type ReferensiOptionsMode = 'exact' | 'prefix';

async function loadReferensiOptions(
  jwt: string,
  kodeKategori: string,
  mode: ReferensiOptionsMode = 'exact',
): Promise<DynamicEntityRow[]> {
  const result = await strapiListDynamicEntityPage(jwt, 'referensis', {
    populate: '*',
    page: 1,
    pageSize: 1000,
    ...(mode === 'exact'
      ? { exactFilters: { kode_kategori: kodeKategori } }
      : { containsFilters: { kode_kategori: kodeKategori } }),
  });
  return result.rows.sort((a, b) => referensiOptionLabel(a).localeCompare(referensiOptionLabel(b), 'id'));
}

function computeMasterGridCellText(row: DynamicEntityRow, c: MasterGridColumn): string {
  if (c.valueGetter) return String(c.valueGetter(row) ?? '') || '';
  return String((row as Record<string, unknown>)[c.field] ?? '') || '';
}

/** Chemical notation is only for parameter name/symbol — never documentId / kode / other ids. */
function isMasterChemDisplayField(field: string): boolean {
  return field === 'nama_parameter' || field === 'simbol' || field === 'parameter_uji';
}

function isMasterFormFieldVisible(ff: MasterFormField, form: Record<string, unknown>): boolean {
  if (ff.kind !== 'number' || !ff.visibleWhen) return true;
  return String(form[ff.visibleWhen.attribute] ?? '') === ff.visibleWhen.equals;
}

function parseJsonField(raw: unknown, label: string): unknown {
  if (typeof raw !== 'string') return raw;
  const t = raw.trim();
  if (!t) throw new Error(`${label}: JSON kosong`);
  try {
    return JSON.parse(t) as unknown;
  } catch {
    throw new Error(`${label}: tidak valid JSON`);
  }
}

function editFormSeed(entry: MasterCrudRegistryEntry, row: DynamicEntityRow): Record<string, unknown> {
  const fm: Record<string, unknown> = {};
  for (const f of entry.formFields) {
    switch (f.kind) {
      case 'boolean':
        fm[f.attribute] = Boolean(row[f.attribute]);
        break;
      case 'relation':
        if (f.relation === 'customer') fm[f.attribute] = relDoc(row.customer);
        else if (f.relation === 'quotation') fm[f.attribute] = relDoc(row.quotation);
        else if (f.relation === 'sample_information')
          fm[f.attribute] = relDoc(row.sample_information);
        else if (f.relation === 'sample_information_point')
          fm[f.attribute] = relDoc(row.sample_information_point);
        else if (f.relation === 'test_result') fm[f.attribute] = relDoc(row.test_result);
        else if (f.relation === 'variable_environment')
          fm[f.attribute] = relDoc(row.environment_variable);
        else if (f.relation === 'user') {
          const u = row.user as Record<string, unknown> | undefined;
          fm[f.attribute] = u?.id != null ? String(u.id) : '';
        } else if (f.relation === 'grouping') fm[f.attribute] = relDoc(row.grouping);
        else if (f.relation === 'jenis_pengukuran') fm[f.attribute] = relDoc(row.jenis_pengukuran);
        else if (f.relation === 'form_question') fm[f.attribute] = relDoc(row.form_question);
        else if (f.relation === 'baku_mutu') fm[f.attribute] = relDoc(row.baku_mutu);
        else if (f.relation === 'parameter_uji') fm[f.attribute] = relDoc(row.parameter_uji);
        else if (f.relation === 'sni_method') fm[f.attribute] = relDoc(row.sni_method_id);
        else if (f.relation === 'referensi_fk') {
          const key = f.referensi_field;
          fm[f.attribute] = key ? relDoc(row[key]) : '';
        } else if (f.relation === 'user_type') {
          fm[f.attribute] = relDoc(row.user_type);
        } else if (f.relation === 'analis_type_data') {
          fm[f.attribute] = relDoc(row.analis_type_data);
        }
        break;
      case 'relation_many':
        fm[f.attribute] = relationFieldToRows(row[f.payload_field]).map((r) => r.documentId);
        break;
      case 'enumeration':
        fm[f.attribute] = String(row[f.attribute] ?? f.options[0] ?? '');
        break;
      case 'json':
        fm[f.attribute] = JSON.stringify(row[f.attribute] ?? {}, null, 2);
        break;
      default: {
        const v = row[f.attribute];
        fm[f.attribute] = v === null || v === undefined ? '' : typeof v === 'number' ? String(v) : String(v);
        break;
      }
    }
  }
  return fm;
}

function buildWritePayload(form: Record<string, unknown>, entry: MasterCrudRegistryEntry): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of entry.formFields) {
    if (!isMasterFormFieldVisible(f, form)) continue;
    switch (f.kind) {
      case 'text':
      case 'email': {
        const v = form[f.attribute];
        const s = typeof v === 'string' ? v.trim() : '';
        if (f.required && !s) throw new Error(`${f.label} wajib diisi.`);
        if (s) out[f.attribute] = s;
        break;
      }
      case 'number': {
        const v = form[f.attribute];
        const s = typeof v === 'string' ? v.trim() : v === null || v === undefined ? '' : String(v);
        if (!s) {
          if (f.required) throw new Error(`${f.label} wajib diisi.`);
          break;
        }
        const num = Number(s);
        if (!Number.isFinite(num)) throw new Error(`${f.label}: angka tidak valid`);
        out[f.attribute] = num;
        break;
      }
      case 'boolean': {
        out[f.attribute] = Boolean(form[f.attribute]);
        break;
      }
      case 'enumeration': {
        const v = String(form[f.attribute] ?? '');
        if (!v && f.required) throw new Error(`${f.label} wajib dipilih.`);
        if (!v) break;
        if (!f.options.includes(v as never)) throw new Error(`${f.label}: pilihan tidak valid (${v}).`);
        out[f.attribute] = v;
        break;
      }
      case 'json': {
        const parsed = parseJsonField(form[f.attribute], f.label);
        if ((parsed === undefined || parsed === null) && f.required) {
          throw new Error(`${f.label} wajib diisi.`);
        }
        if (parsed !== undefined) out[f.attribute] = parsed;
        break;
      }
      case 'relation': {
        const raw = String(form[f.attribute] ?? '').trim();
        if (f.required && !raw) throw new Error(`${f.label} wajib dipilih.`);
        const q = raw;
        if (f.relation === 'customer') {
          const w = customerRelationWrite(q || undefined);
          if (w) out.customer = w;
        } else if (f.relation === 'quotation') {
          const w = connectPayload(q);
          if (w) out.quotation = w;
        } else if (f.relation === 'sample_information') {
          const w = connectPayload(q);
          if (w) out.sample_information = w;
        } else if (f.relation === 'sample_information_point') {
          const w = connectPayload(q);
          if (w) out.sample_information_point = w;
        } else if (f.relation === 'test_result') {
          const w = connectPayload(q);
          if (w) out.test_result = w;
        } else if (f.relation === 'variable_environment') {
          const w = connectPayload(q);
          if (w) out.environment_variable = w;
        } else if (f.relation === 'user') {
          const nid = Number(q);
          const w = userRelationWrite(undefined, Number.isFinite(nid) ? nid : null);
          if (w !== undefined && w !== null) out.user = w;
        } else if (f.relation === 'grouping') {
          const w = connectPayload(q);
          if (w) out.grouping = w;
        } else if (f.relation === 'jenis_pengukuran') {
          const w = connectPayload(q);
          if (w) out.jenis_pengukuran = w;
        } else if (f.relation === 'form_question') {
          const w = connectPayload(q);
          if (w) out.form_question = w;
        } else if (f.relation === 'baku_mutu') {
          const w = connectPayload(q);
          if (w) out.baku_mutu = w;
        } else if (f.relation === 'parameter_uji') {
          const w = connectPayload(q);
          if (w) out.parameter_uji = w;
        } else if (f.relation === 'sni_method') {
          out.sni_method_id = { set: q ? [q] : [] };
        } else if (f.relation === 'referensi_fk') {
          const key = f.referensi_field;
          if (key) {
            const w = connectPayload(q);
            if (w) out[key] = w;
          }
        } else if (f.relation === 'user_type') {
          const w = connectPayload(q);
          if (w) out.user_type = w;
        } else if (f.relation === 'analis_type_data') {
          out.analis_type_data = { set: q ? [q] : [] };
        }
        break;
      }
      case 'relation_many': {
        const raw = form[f.attribute];
        const ids = Array.isArray(raw)
          ? raw.map((v) => String(v ?? '').trim()).filter(Boolean)
          : [];
        if (f.required && ids.length === 0) throw new Error(`${f.label} wajib dipilih.`);
        out[f.payload_field] =
          f.payload_field === 'jenis_pengukuran' || f.payload_field === 'form_question'
            ? { set: ids }
            : { connect: ids };
        break;
      }
      default:
        break;
    }
  }
  return out;
}

function emptyForm(entry: MasterCrudRegistryEntry): Record<string, unknown> {
  const fm: Record<string, unknown> = {};
  for (const f of entry.formFields) {
    if (f.kind === 'boolean') fm[f.attribute] = false;
    else if (f.kind === 'enumeration') fm[f.attribute] = f.options[0] ?? '';
    else if (f.kind === 'relation_many') fm[f.attribute] = [];
    else fm[f.attribute] = '';
  }
  return fm;
}

export function MastersEntityCrudPage() {
  const { entitySlug } = useParams<{ entitySlug: string }>();
  const slug = entitySlug ? decodeURIComponent(entitySlug) : '';
  const { jwt } = useAuth();
  const registryDef = MASTER_CRUD_REGISTRY[slug];
  const referensiKodeKategori = !registryDef ? kodeKategoriFromReferensiSlug(slug) : null;
  const def = useMemo(
    () =>
      registryDef ??
      (referensiKodeKategori
        ? createReferensiMasterGroupEntry({
            slug,
            title: labelFromReferensiKodeKategori(referensiKodeKategori),
            kodeKategori: referensiKodeKategori,
          })
        : undefined),
    [registryDef, referensiKodeKategori, slug],
  );

  useDocumentTitle(appDocumentTitle(def?.title ?? 'Master data'));
  const qc = useQueryClient();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: DATA_GRID_DEFAULT_PAGE_SIZE });

  const [bakuKodeFilter, setBakuKodeFilter] = useState('');
  const [bakuNamaFilter, setBakuNamaFilter] = useState('');
  const [bakuJudulFilter, setBakuJudulFilter] = useState('');
  const [debouncedBakuFilters, setDebouncedBakuFilters] = useState({
    kode: '',
    nama_baku_mutu: '',
    judul: '',
  });

  const [refKodeKategoriFilter, setRefKodeKategoriFilter] = useState('');
  const [refKodeFilter, setRefKodeFilter] = useState('');
  const [refParentFilter, setRefParentFilter] = useState('');
  const [refStatusFilter, setRefStatusFilter] = useState('');
  const [debouncedReferensiFilters, setDebouncedReferensiFilters] = useState({
    kode_kategori: '',
    kode: '',
    grouping: '',
    active_status: '',
  });

  const [jpGroupingIdFilter, setJpGroupingIdFilter] = useState('');
  const [jpNamaFilter, setJpNamaFilter] = useState('');
  const [jpKodeFilter, setJpKodeFilter] = useState('');
  const [debouncedJenisPengukuranFilters, setDebouncedJenisPengukuranFilters] = useState({
    'grouping.documentId': '',
    nama_jenis: '',
    kode_jenis: '',
  });

  const [fqNamaFormFilter, setFqNamaFormFilter] = useState('');
  const [debouncedFqNamaFormFilter, setDebouncedFqNamaFormFilter] = useState('');
  const [fqJenisFilterIds, setFqJenisFilterIds] = useState<string[]>([]);
  const [fqSelection, setFqSelection] = useState<GridRowSelectionModel>([]);
  const [fqoSelection, setFqoSelection] = useState<GridRowSelectionModel>([]);

  const [bmpuNamaParameterFilter, setBmpuNamaParameterFilter] = useState('');
  const [bmpuNamaBakuMutuFilter, setBmpuNamaBakuMutuFilter] = useState('');
  const [bmpuJudulBakuMutuFilter, setBmpuJudulBakuMutuFilter] = useState('');
  const [debouncedBmpuFilters, setDebouncedBmpuFilters] = useState({
    nama_parameter: '',
    nama_baku_mutu: '',
    judul: '',
  });

  const [pumNamaParameterFilter, setPumNamaParameterFilter] = useState('');
  const [debouncedPumNamaParameterFilter, setDebouncedPumNamaParameterFilter] = useState('');
  const [puNamaParameterFilter, setPuNamaParameterFilter] = useState('');
  const [debouncedPuNamaParameterFilter, setDebouncedPuNamaParameterFilter] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedBakuFilters({
        kode: bakuKodeFilter.trim(),
        nama_baku_mutu: bakuNamaFilter.trim(),
        judul: bakuJudulFilter.trim(),
      });
    }, 400);
    return () => window.clearTimeout(id);
  }, [bakuKodeFilter, bakuNamaFilter, bakuJudulFilter]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedReferensiFilters({
        kode_kategori: refKodeKategoriFilter.trim(),
        kode: refKodeFilter.trim(),
        grouping: refParentFilter.trim(),
        active_status: refStatusFilter.trim(),
      });
    }, 400);
    return () => window.clearTimeout(id);
  }, [refKodeKategoriFilter, refKodeFilter, refParentFilter, refStatusFilter]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedJenisPengukuranFilters({
        'grouping.documentId': jpGroupingIdFilter.trim(),
        nama_jenis: jpNamaFilter.trim(),
        kode_jenis: jpKodeFilter.trim(),
      });
    }, 400);
    return () => window.clearTimeout(id);
  }, [jpGroupingIdFilter, jpNamaFilter, jpKodeFilter]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedFqNamaFormFilter(fqNamaFormFilter.trim());
    }, 400);
    return () => window.clearTimeout(id);
  }, [fqNamaFormFilter]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedBmpuFilters({
        nama_parameter: bmpuNamaParameterFilter.trim(),
        nama_baku_mutu: bmpuNamaBakuMutuFilter.trim(),
        judul: bmpuJudulBakuMutuFilter.trim(),
      });
    }, 400);
    return () => window.clearTimeout(id);
  }, [bmpuNamaParameterFilter, bmpuNamaBakuMutuFilter, bmpuJudulBakuMutuFilter]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedPumNamaParameterFilter(pumNamaParameterFilter.trim());
    }, 400);
    return () => window.clearTimeout(id);
  }, [pumNamaParameterFilter]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedPuNamaParameterFilter(puNamaParameterFilter.trim());
    }, 400);
    return () => window.clearTimeout(id);
  }, [puNamaParameterFilter]);

  const listContainsFilters = useMemo((): Partial<Record<string, string>> | undefined => {
    const baseFilters = def?.fixedListFilters ? { ...def.fixedListFilters } : {};
    if (slug === 'baku-mutus') {
      const out: Partial<Record<string, string>> = { ...baseFilters };
      if (debouncedBakuFilters.kode) out.kode = debouncedBakuFilters.kode;
      if (debouncedBakuFilters.nama_baku_mutu) out.nama_baku_mutu = debouncedBakuFilters.nama_baku_mutu;
      if (debouncedBakuFilters.judul) out.judul = debouncedBakuFilters.judul;
      return Object.keys(out).length ? out : undefined;
    }
    if (slug === 'referensis') {
      const out: Partial<Record<string, string>> = { ...baseFilters };
      if (debouncedReferensiFilters.kode_kategori) out.kode_kategori = debouncedReferensiFilters.kode_kategori;
      if (debouncedReferensiFilters.kode) out.kode = debouncedReferensiFilters.kode;
      if (debouncedReferensiFilters.grouping) out.grouping = debouncedReferensiFilters.grouping;
      if (debouncedReferensiFilters.active_status) out.active_status = debouncedReferensiFilters.active_status;
      return Object.keys(out).length ? out : undefined;
    }
    if (slug === 'jenis-pengukurans') {
      const out: Partial<Record<string, string>> = { ...baseFilters };
      if (debouncedJenisPengukuranFilters['grouping.documentId']) {
        out['grouping.documentId'] = debouncedJenisPengukuranFilters['grouping.documentId'];
      }
      if (debouncedJenisPengukuranFilters.nama_jenis) out.nama_jenis = debouncedJenisPengukuranFilters.nama_jenis;
      if (debouncedJenisPengukuranFilters.kode_jenis) out.kode_jenis = debouncedJenisPengukuranFilters.kode_jenis;
      return Object.keys(out).length ? out : undefined;
    }
    if (slug === 'form-questions') {
      const out: Partial<Record<string, string>> = { ...baseFilters };
      if (debouncedFqNamaFormFilter) out.form_question_name = debouncedFqNamaFormFilter;
      if (fqJenisFilterIds.length) out['jenis_pengukuran.documentId'] = fqJenisFilterIds.join(',');
      return Object.keys(out).length ? out : undefined;
    }
    if (slug === 'baku-mutu-parameter-ujis') {
      const out: Partial<Record<string, string>> = { ...baseFilters };
      if (debouncedBmpuFilters.nama_parameter) {
        out['parameter_uji.nama_parameter'] = debouncedBmpuFilters.nama_parameter;
      }
      if (debouncedBmpuFilters.nama_baku_mutu) {
        out['baku_mutu.nama_baku_mutu'] = debouncedBmpuFilters.nama_baku_mutu;
      }
      if (debouncedBmpuFilters.judul) {
        out['baku_mutu.judul'] = debouncedBmpuFilters.judul;
      }
      return Object.keys(out).length ? out : undefined;
    }
    if (slug === 'parameter-uji-metodas') {
      const out: Partial<Record<string, string>> = { ...baseFilters };
      if (debouncedPumNamaParameterFilter) {
        out.parameter_uji = debouncedPumNamaParameterFilter;
      }
      return Object.keys(out).length ? out : undefined;
    }
    if (slug === 'parameter-ujis') {
      const out: Partial<Record<string, string>> = { ...baseFilters };
      if (debouncedPuNamaParameterFilter) {
        out.parameter = debouncedPuNamaParameterFilter;
      }
      return Object.keys(out).length ? out : undefined;
    }
    return Object.keys(baseFilters).length ? baseFilters : undefined;
  }, [
    def?.fixedListFilters,
    slug,
    debouncedBakuFilters,
    debouncedReferensiFilters,
    debouncedJenisPengukuranFilters,
    debouncedFqNamaFormFilter,
    fqJenisFilterIds,
    debouncedBmpuFilters,
    debouncedPumNamaParameterFilter,
    debouncedPuNamaParameterFilter,
  ]);

  const hasActiveListFilters =
    Boolean(
      debouncedBakuFilters.kode ||
        debouncedBakuFilters.nama_baku_mutu ||
        debouncedBakuFilters.judul,
    ) ||
    Boolean(
      debouncedReferensiFilters.kode_kategori ||
        debouncedReferensiFilters.kode ||
        debouncedReferensiFilters.grouping ||
        debouncedReferensiFilters.active_status,
    ) ||
    Boolean(
      debouncedJenisPengukuranFilters['grouping.documentId'] ||
        debouncedJenisPengukuranFilters.nama_jenis ||
        debouncedJenisPengukuranFilters.kode_jenis,
    ) ||
    Boolean(debouncedFqNamaFormFilter || fqJenisFilterIds.length) ||
    Boolean(
      debouncedBmpuFilters.nama_parameter ||
        debouncedBmpuFilters.nama_baku_mutu ||
        debouncedBmpuFilters.judul,
    ) ||
    Boolean(debouncedPumNamaParameterFilter) ||
    Boolean(debouncedPuNamaParameterFilter);

  useEffect(() => {
    if (
      slug !== 'baku-mutus' &&
      slug !== 'referensis' &&
      slug !== 'jenis-pengukurans' &&
      slug !== 'form-questions' &&
      slug !== 'baku-mutu-parameter-ujis' &&
      slug !== 'parameter-uji-metodas' &&
      slug !== 'parameter-ujis'
    ) {
      return;
    }
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [
    debouncedBakuFilters.kode,
    debouncedBakuFilters.nama_baku_mutu,
    debouncedBakuFilters.judul,
    debouncedReferensiFilters.kode_kategori,
    debouncedReferensiFilters.kode,
    debouncedReferensiFilters.grouping,
    debouncedReferensiFilters.active_status,
    debouncedJenisPengukuranFilters['grouping.documentId'],
    debouncedJenisPengukuranFilters.nama_jenis,
    debouncedJenisPengukuranFilters.kode_jenis,
    debouncedFqNamaFormFilter,
    fqJenisFilterIds,
    debouncedBmpuFilters.nama_parameter,
    debouncedBmpuFilters.nama_baku_mutu,
    debouncedBmpuFilters.judul,
    debouncedPumNamaParameterFilter,
    debouncedPuNamaParameterFilter,
    slug,
  ]);

  useEffect(() => {
    setFqSelection([]);
    setFqoSelection([]);
  }, [slug, debouncedFqNamaFormFilter, fqJenisFilterIds]);

  const listQ = useMasterEntityListQuery(
    jwt,
    slug,
    slug === 'form-questions' ? 1 : paginationModel.page + 1,
    slug === 'form-questions' ? FORM_QUESTION_LIST_FETCH_SIZE : paginationModel.pageSize,
    listContainsFilters,
    def,
  );
  const createMut = useMasterEntityCreateMutation(jwt, slug, def);
  const updateMut = useMasterEntityUpdateMutation(jwt, slug, def);
  const deleteMut = useMasterEntityDeleteMutation(jwt, slug, def);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DynamicEntityRow | null>(null);
  const [fqDraft, setFqDraft] = useState<FormQuestionGroupDraft | null>(null);
  const [ptpDraft, setPtpDraft] = useState<PeralatanToParameterUjiDraft | null>(null);
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState<FormQuestionGroup | null>(null);
  const [fqSaving, setFqSaving] = useState(false);

  useEffect(() => {
    setPendingDeleteGroup(null);
  }, [slug, debouncedFqNamaFormFilter, fqJenisFilterIds]);

  const customersQ = useCustomersQuery(jwt);
  const quotationsQ = useQuotationsQuery(jwt);
  const samplesQ = useSampleInformationsListQuery(jwt);
  const samplesPointNeeded = Boolean(
    def?.formFields.some((ff) => ff.kind === 'relation' && ff.relation === 'sample_information_point'),
  );
  const samplesPointQ = useMasterEntityMiniListQuery(
    jwt,
    'sample-information-points',
    Boolean(jwt) && samplesPointNeeded,
  );
  const testResultsQ = useTestResultsListQuery(jwt, true);
  const usersQ = useUsersQuery(jwt, { emailContains: '', status: 'all' });
  const userTypeNeeded = Boolean(
    def?.formFields.some((f) => f.kind === 'relation' && f.relation === 'user_type'),
  );
  const userTypesQ = useUserTypesQuery(userTypeNeeded ? jwt : null);
  const analisTypeDataNeeded = Boolean(
    def?.formFields.some((f) => f.kind === 'relation' && f.relation === 'analis_type_data'),
  );
  const analisTypeDataQ = useMasterEntityMiniListQuery(
    jwt,
    'analis-type-datas',
    Boolean(jwt) && analisTypeDataNeeded,
  );

  const envVarNeeded = Boolean(
    def?.formFields.some((f) => f.kind === 'relation' && f.relation === 'variable_environment'),
  );
  const envVarQ = useMasterEntityMiniListQuery(jwt, 'variable-environment-conditions', Boolean(jwt) && envVarNeeded);

  const labGroupingNeeded = Boolean(
    def?.formFields.some((f) => f.kind === 'relation' && f.relation === 'grouping'),
  );
  const labGroupingQ = useMasterEntityMiniListQuery(jwt, 'groupings', Boolean(jwt) && labGroupingNeeded);

  const labJenisNeeded = Boolean(
    def?.formFields.some(
      (f) =>
        (f.kind === 'relation' && f.relation === 'jenis_pengukuran') ||
        (f.kind === 'relation_many' && f.payload_field === 'jenis_pengukuran'),
    ) || slug === 'form-questions',
  );
  const labJenisQ = useMasterEntityMiniListQuery(jwt, 'jenis-pengukurans', Boolean(jwt) && labJenisNeeded);

  const labFormQuestionNeeded = Boolean(
    def?.formFields.some(
      (f) =>
        (f.kind === 'relation' && f.relation === 'form_question') ||
        (f.kind === 'relation_many' && f.payload_field === 'form_question'),
    ) || slug === 'form-question-options',
  );
  const labFormQuestionQ = useMasterEntityMiniListQuery(
    jwt,
    'form-questions',
    Boolean(jwt) && labFormQuestionNeeded,
  );
  const formQuestionNameOptionsQ = useMasterEntityMiniListQuery(
    jwt,
    'form-questions',
    Boolean(jwt && slug === 'form-questions'),
  );
  const formQuestionNameFilterOptions = useMemo(
    () => uniqueFormQuestionNames(formQuestionNameOptionsQ.data ?? []),
    [formQuestionNameOptionsQ.data],
  );

  const labBakuMutuNeeded = Boolean(
    def?.formFields.some((f) => f.kind === 'relation' && f.relation === 'baku_mutu'),
  );
  const labBakuMutuQ = useMasterEntityMiniListQuery(jwt, 'baku-mutus', Boolean(jwt) && labBakuMutuNeeded);

  const labReferensiNeeded = Boolean(
    def?.formFields.some((f) => f.kind === 'relation' && f.relation === 'referensi_fk'),
  );
  const createReferensiCategoryMode = Boolean(
    editorMode === 'create' && def?.exactListFilters?.kode_kategori,
  );
  const labReferensiQ = useMasterEntityMiniListQuery(
    jwt,
    'referensis',
    Boolean(jwt) && labReferensiNeeded && editorOpen && !createReferensiCategoryMode,
  );

  const labParameterUjiNeeded = Boolean(
    def?.formFields.some((f) => f.kind === 'relation' && f.relation === 'parameter_uji'),
  );
  const labParameterUjiQ = useMasterEntityMiniListQuery(jwt, 'parameter-ujis', Boolean(jwt) && labParameterUjiNeeded);

  const labSniMethodNeeded = Boolean(
    def?.formFields.some((f) => f.kind === 'relation' && f.relation === 'sni_method'),
  );
  const labSniMethodQ = useMasterEntityMiniListQuery(jwt, 'sni-methods', Boolean(jwt) && labSniMethodNeeded);

  const jpPerlengkapanEditorOpen = slug === 'jenis-pengukuran-perlengkapans' && editorOpen;
  const peralatanPengambilanContohQ = useQuery({
    queryKey: ['strapi', 'referensiOptions', jwt, 'jp-perlengkapan-pengambilan', 'PERALATAN_SAMPLING'],
    queryFn: () => loadReferensiOptions(jwt!, 'PERALATAN_SAMPLING', 'prefix'),
    enabled: Boolean(jwt && jpPerlengkapanEditorOpen),
  });
  const peralatanPengukuranLapanganQ = useQuery({
    queryKey: ['strapi', 'referensiOptions', jwt, 'jp-perlengkapan-ukur-lap', 'PERALATAN_UKUR_LAP'],
    queryFn: () => loadReferensiOptions(jwt!, 'PERALATAN_UKUR_LAP'),
    enabled: Boolean(jwt && jpPerlengkapanEditorOpen),
  });
  const peralatanPendukungQ = useQuery({
    queryKey: ['strapi', 'referensiOptions', jwt, 'jp-perlengkapan-pendukung', 'PERALATAN_PENDUKUNG'],
    queryFn: () => loadReferensiOptions(jwt!, 'PERALATAN_PENDUKUNG'),
    enabled: Boolean(jwt && jpPerlengkapanEditorOpen),
  });
  const peralatanK3Q = useQuery({
    queryKey: ['strapi', 'referensiOptions', jwt, 'jp-perlengkapan-k3', 'PERALATAN_K3'],
    queryFn: () => loadReferensiOptions(jwt!, 'PERALATAN_K3'),
    enabled: Boolean(jwt && jpPerlengkapanEditorOpen),
  });

  const referensiOptionsByKategori = useMemo(
    (): Record<string, DynamicEntityRow[]> => ({
      PERALATAN_SAMPLING: peralatanPengambilanContohQ.data ?? [],
      PERALATAN_UKUR_LAP: peralatanPengukuranLapanganQ.data ?? [],
      PERALATAN_PENDUKUNG: peralatanPendukungQ.data ?? [],
      PERALATAN_K3: peralatanK3Q.data ?? [],
    }),
    [
      peralatanPengambilanContohQ.data,
      peralatanPengukuranLapanganQ.data,
      peralatanPendukungQ.data,
      peralatanK3Q.data,
    ],
  );

  const fqGroups = useMemo(
    () => (slug === 'form-questions' ? groupFormQuestionsByName(listQ.data?.rows ?? []) : []),
    [slug, listQ.data?.rows],
  );
  const fqPaged = useMemo(
    () => paginateFormQuestionGroups(fqGroups, paginationModel.page, paginationModel.pageSize),
    [fqGroups, paginationModel.page, paginationModel.pageSize],
  );
  const rows =
    slug === 'form-questions'
      ? fqPaged.rows.map((group) => ({
          documentId: formQuestionGroupKey(group.name),
          form_question_name: group.name,
          question_count: group.questions.length,
          questions: group.questions,
        }))
      : (listQ.data?.rows ?? []);
  const rowCount = slug === 'form-questions' ? fqPaged.total : (listQ.data?.pagination?.total ?? 0);
  const fqSelectedIds = useMemo(
    () =>
      slug === 'form-questions'
        ? documentIdsInFormQuestionGroups(rowSelectionDocumentIds(fqSelection), fqGroups)
        : rowSelectionDocumentIds(fqSelection),
    [slug, fqSelection, fqGroups],
  );
  const fqoSelectedIds = useMemo(() => rowSelectionDocumentIds(fqoSelection), [fqoSelection]);

  const openCreate = useCallback(() => {
    if (!def) return;
    setEditorMode('create');
    setEditingId(null);
    setForm(emptyForm(def));
    setFqDraft(slug === 'form-questions' ? emptyFormQuestionGroupDraft() : null);
    setPtpDraft(slug === 'peralatan-to-parameter-ujis' ? emptyPeralatanToParameterUjiDraft() : null);
    setFormError(null);
    setEditorOpen(true);
  }, [def, slug]);

  const openFormQuestionGroup = useCallback(
    (groupName: string) => {
      const source = formQuestionNameOptionsQ.data?.length ? formQuestionNameOptionsQ.data : (listQ.data?.rows ?? []);
      const questions = questionsForFormName(source, groupName);
      setEditorMode('edit');
      setEditingId(formQuestionGroupKey(groupName));
      setFqDraft(draftFromFormQuestionGroup(groupName, questions));
      setFormError(null);
      setEditorOpen(true);
    },
    [formQuestionNameOptionsQ.data, listQ.data?.rows],
  );

  const openEdit = useCallback(
    (rowRaw: DynamicEntityRow) => {
      if (!def) return;
      if (slug === 'form-questions') {
        const name = String(rowRaw.form_question_name ?? '').trim();
        if (!name) return;
        openFormQuestionGroup(name);
        return;
      }
      const row = normalizeDynamicRow(rowRaw as Record<string, unknown>);
      setEditorMode('edit');
      setEditingId(row.documentId);
      setForm(editFormSeed(def, row));
      setFqDraft(null);
      setPtpDraft(slug === 'peralatan-to-parameter-ujis' ? draftFromPeralatanToParameterUjiRow(row) : null);
      setFormError(null);
      setEditorOpen(true);
    },
    [def, openFormQuestionGroup, slug],
  );

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setFormError(null);
    setFqDraft(null);
    setPtpDraft(null);
    setFqSaving(false);
  }, []);

  async function submitFormQuestionGroup() {
    if (!jwt || !fqDraft) return;
    if (formQuestionNameTaken(fqDraft.formName, formQuestionNameFilterOptions, fqDraft.originalName)) {
      throw new Error('Nama form sudah dipakai. Edit form yang ada untuk menambah pertanyaan.');
    }
    const plan = planFormQuestionGroupSave(fqDraft);
    for (const payload of plan.toCreate) {
      await strapiCreateDynamicEntity(jwt, 'form-questions', payload, '*');
    }
    for (const item of plan.toUpdate) {
      await strapiUpdateDynamicEntity(jwt, 'form-questions', item.documentId, item.data, '*');
    }
    for (const documentId of plan.toDelete) {
      await strapiDeleteDynamicEntity(jwt, 'form-questions', documentId);
    }

    await Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.masterEntityListPrefix(jwt, 'form-questions') }),
      qc.invalidateQueries({ queryKey: queryKeys.masterEntityMini(jwt, 'form-questions') }),
    ]);
  }

  async function submitEditor(e: React.FormEvent) {
    e.preventDefault();
    if (!def || !jwt) return;
    setFormError(null);
    try {
      if (slug === 'form-questions') {
        setFqSaving(true);
        await submitFormQuestionGroup();
        closeEditor();
        return;
      }
      if (slug === 'peralatan-to-parameter-ujis') {
        if (!ptpDraft) throw new Error('Form belum siap.');
        const parameterId = String(ptpDraft.parameter_uji?.documentId ?? '').trim();
        const existingId = parameterId
          ? await strapiFindPeralatanToParameterUjiDocumentId(jwt, parameterId)
          : null;
        if (existingId && existingId !== editingId) {
          throw new Error('Parameter uji ini sudah punya mapping. Buka baris yang sudah ada untuk mengubahnya.');
        }
        const payload = buildPeralatanToParameterUjiWritePayload(ptpDraft);
        if (editorMode === 'edit' && editingId) {
          await updateMut.mutateAsync({ documentId: editingId, data: payload });
        } else {
          await createMut.mutateAsync(payload);
        }
        closeEditor();
        return;
      }
      const payload =
        slug === 'form-question-options'
          ? applyFormQuestionOptionRangeWrite(buildWritePayload(form, def))
          : buildWritePayload(form, def);
      if (editorMode === 'create') {
        await createMut.mutateAsync({ ...(def.createDefaults ?? {}), ...payload });
      } else if (editingId) {
        await updateMut.mutateAsync({ documentId: editingId, data: payload });
      }
      closeEditor();
    } catch (err) {
      setFqSaving(false);
      setFormError(err instanceof Error ? err.message : 'Request failed.');
    }
  }

  async function confirmDelete() {
    if (slug === 'form-questions' && pendingDeleteGroup && jwt) {
      try {
        for (const question of pendingDeleteGroup.questions) {
          if (!question.documentId) continue;
          await deleteMut.mutateAsync(question.documentId);
        }
        await qc.invalidateQueries({ queryKey: queryKeys.masterEntityMini(jwt, 'form-questions') });
        deleteMut.reset();
        setPendingDeleteGroup(null);
      } catch {
        /* Dialog shows error */
      }
      return;
    }
    if (!pendingDelete?.documentId || !jwt) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.documentId);
      deleteMut.reset();
      setPendingDelete(null);
    } catch {
      /* Dialog shows error */
    }
  }

  const columns: GridColDef<DynamicEntityRow>[] = useMemo(() => {
    if (!def) return [];
    if (slug === 'form-questions') {
      return [
        {
          field: 'form_question_name',
          headerName: 'Nama form',
          flex: 0.32,
          minWidth: 220,
          valueGetter: (_value: unknown, row: DynamicEntityRow) => {
            const questions = Array.isArray(row.questions) ? (row.questions as DynamicEntityRow[]) : [];
            return [String(row.form_question_name ?? ''), ...uniqueJenisPengukuranLabels(questions)]
              .filter(Boolean)
              .join(' ');
          },
          renderCell: (params) => {
            const name = String(params.row?.form_question_name ?? '').trim() || '—';
            const questions = Array.isArray(params.row?.questions)
              ? (params.row.questions as DynamicEntityRow[])
              : [];
            const jenisLabels = uniqueJenisPengukuranLabels(questions);
            return (
              <Box sx={{ py: 0.75, pr: 1, lineHeight: 1.35 }}>
                <Box sx={{ fontWeight: 600 }}>{name}</Box>
                <Box sx={{ color: 'text.secondary', fontSize: 12, mt: 0.25, whiteSpace: 'normal' }}>
                  {jenisLabels.length ? jenisLabels.join(', ') : 'Belum dipetakan ke jenis pengukuran'}
                </Box>
              </Box>
            );
          },
        },
        {
          field: 'questions',
          headerName: 'Pertanyaan',
          flex: 0.68,
          minWidth: 420,
          sortable: false,
          valueGetter: (_value: unknown, row: DynamicEntityRow) => {
            const questions = Array.isArray(row.questions) ? (row.questions as DynamicEntityRow[]) : [];
            return questions.map((q) => String(q.title ?? '')).filter(Boolean).join(', ');
          },
          renderCell: (params) => {
            const questions = Array.isArray(params.row?.questions)
              ? (params.row.questions as DynamicEntityRow[])
              : [];
            if (!questions.length) return '—';
            return (
              <Box
                component="table"
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  my: 0.5,
                  '& th, & td': {
                    textAlign: 'left',
                    verticalAlign: 'top',
                    fontSize: 12,
                    py: 0.35,
                    pr: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                  '& th': { color: 'text.secondary', fontWeight: 600, borderBottomWidth: 2 },
                }}
              >
                <thead>
                  <tr>
                    <th>Judul</th>
                    <th>Tipe jawaban</th>
                    <th>Nilai default</th>
                    <th>Urutan</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q) => (
                    <tr key={q.documentId}>
                      <td>{String(q.title ?? '') || '—'}</td>
                      <td>{String(q.answer_type ?? '') || '—'}</td>
                      <td>{String(q.default_value ?? '').trim() || '—'}</td>
                      <td>{q.sort_order == null ? '—' : String(q.sort_order)}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            );
          },
        },
        {
          field: 'actions_row',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          getActions: (params) => {
            const row = params.row;
            if (!row) return [];
            const name = String(row.form_question_name ?? '').trim();
            const questions = Array.isArray(row.questions) ? (row.questions as DynamicEntityRow[]) : [];
            return [
              <GridActionsCellItem
                key="e"
                icon={<EditOutlinedIcon fontSize="small" />}
                label="Edit"
                onClick={() => openEdit(row)}
              />,
              <GridActionsCellItem
                key="d"
                icon={<DeleteOutlineIcon fontSize="small" />}
                label="Delete"
                onClick={() => setPendingDeleteGroup({ name, questions })}
              />,
            ];
          },
        },
      ];
    }
    const base: GridColDef<DynamicEntityRow>[] = def.columns.map((c) => ({
      field: c.field,
      headerName: c.headerName,
      flex: c.flex,
      minWidth: c.minWidth,
      /** MUI X v7: `(value, row, column, api)`, not `{ row }`-style params */
      valueGetter: (_cellValue: unknown, row: DynamicEntityRow) => {
        if (row == null) return '';
        return computeMasterGridCellText(row, c);
      },
      renderCell: (params) => {
        const row = params.row;
        if (!row) return null;
        const s = computeMasterGridCellText(row, c);
        if (c.field === 'jenis_pengukuran') {
          const lines = s.split('\n').map((line) => line.trim()).filter(Boolean);
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.25,
                py: 0.5,
                minHeight: '100%',
                overflow: 'visible',
              }}
            >
              {lines.length
                ? lines.map((line, i) => (
                    <Box key={`${line}-${i}`} component="span" sx={{ whiteSpace: 'nowrap' }}>
                      {line}
                    </Box>
                  ))
                : '—'}
            </Box>
          );
        }
        return (
          <Box sx={{ lineHeight: 1.25, py: 0.25, minHeight: '100%', alignContent: 'center' }}>
            {isMasterChemDisplayField(c.field) ? <ChemInline text={s} /> : s}
          </Box>
        );
      },
    }));
    base.push({
      field: 'actions_row',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => {
        const row = params.row;
        if (!row) return [];
        return [
          <GridActionsCellItem
            key="e"
            icon={<EditOutlinedIcon fontSize="small" />}
            label="Edit"
            onClick={() => openEdit(row)}
          />,
          <GridActionsCellItem
            key="d"
            icon={<DeleteOutlineIcon fontSize="small" />}
            label="Delete"
            onClick={() => setPendingDelete(row)}
          />,
        ];
      },
    });
    return base;
  }, [def, openEdit, slug]);

  const renderRelationField = useCallback(
    (f: MasterFormField & { kind: 'relation' }) => {
      if (!def) return null;
      if (f.relation === 'customer') {
        const opts = customersQ.data?.rows ?? [];
        const val = opts.find((c) => c.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(o) => `${o.customer_number} — ${o.name}`}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'quotation') {
        const opts = quotationsQ.data?.rows ?? [];
        const val =
          opts.find((q) => q.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(q) => `${q.quotation_number} (${q.customer?.name ?? '-'})`}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'sample_information') {
        const opts = samplesQ.data?.rows ?? [];
        const val = opts.find((s) => s.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(s) => `${s.jenis_pengukuran ?? '(sample)'} — ${s.parameter_uji ?? ''}`}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'sample_information_point') {
        const opts = samplesPointQ.data ?? [];
        const val = opts.find((p) => p.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={samplesPointQ.isPending}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(row) => {
              const r = row as Record<string, unknown>;
              const si = r.sample_information as Record<string, unknown> | undefined;
              const did = String(r.documentId ?? '');
              if (si && typeof si === 'object') {
                return `${String(si.jenis_pengukuran ?? '—')} · ${String(si.parameter_uji ?? '—')} (${did.slice(0, 8)}…)`;
              }
              return did;
            }}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'test_result') {
        const opts = testResultsQ.data?.rows ?? [];
        const val = opts.find((t) => t.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(tr) =>
              `${[tr.test_item, tr.test_method].filter(Boolean).join(' · ') || 'Test result'} — ${tr.documentId.slice(0, 10)}…`
            }
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'variable_environment') {
        const opts = envVarQ.data ?? [];
        const val = opts.find((v) => v.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={envVarQ.isPending}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(row) => String(row.name ?? row.documentId)}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'user') {
        const opts = usersQ.data?.rows ?? [];
        const nid = Number(form[f.attribute]);
        const val =
          opts.find((u) => (Number.isFinite(nid) ? u.numericId === nid : false)) ??
          opts.find((u) => u.id === String(form[f.attribute])) ??
          null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            onChange={(_, v: StrapiManagedUserRow | null) =>
              setForm((p) => ({ ...p, [f.attribute]: v?.numericId != null ? String(v.numericId) : '' }))
            }
            getOptionLabel={(u) => `${u.username} (${u.email})`}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'grouping') {
        const opts = labGroupingQ.data ?? [];
        const val = opts.find((row) => row.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={labGroupingQ.isPending}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(row) => groupingOptionLabel(row)}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'jenis_pengukuran') {
        const opts = labJenisQ.data ?? [];
        const val = opts.find((row) => row.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={labJenisQ.isPending}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(row) => jenisPengukuranOptionLabel(row)}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'form_question') {
        const opts = labFormQuestionQ.data ?? [];
        const val = opts.find((row) => row.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={labFormQuestionQ.isPending}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(row) => formQuestionMasterLabel(row)}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'baku_mutu') {
        const opts = labBakuMutuQ.data ?? [];
        const val = opts.find((row) => row.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={labBakuMutuQ.isPending}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(row) => bakuMutuOptionLabel(row)}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'parameter_uji') {
        const opts = labParameterUjiQ.data ?? [];
        const val = opts.find((row) => row.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={labParameterUjiQ.isPending}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(row) => parameterUjiOptionLabel(row)}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'sni_method') {
        const opts = labSniMethodQ.data ?? [];
        const val = opts.find((row) => row.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={labSniMethodQ.isPending}
            onChange={(_, v) =>
              setForm((p) => ({
                ...p,
                [f.attribute]: v?.documentId ?? '',
                kode_sni: v ? String(v.kode_sni ?? '').trim() : String(p.kode_sni ?? ''),
                sni_id:
                  v && v.legacy_mysql_sni_id != null && String(v.legacy_mysql_sni_id).trim()
                    ? String(v.legacy_mysql_sni_id)
                    : p.sni_id,
              }))
            }
            getOptionLabel={(row) => sniMethodOptionLabel(row)}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'referensi_fk') {
        const fixedKodeKategori = def.exactListFilters?.kode_kategori;
        if (editorMode === 'create' && f.referensi_field === 'grouping' && fixedKodeKategori) {
          return (
            <TextField
              label={f.label}
              size="small"
              fullWidth
              disabled
              value={labelFromReferensiKodeKategori(fixedKodeKategori)}
              helperText={`Otomatis mengikuti kode kategori ${fixedKodeKategori}`}
            />
          );
        }
        const opts = labReferensiQ.data ?? [];
        const val = opts.find((row) => row.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={labReferensiQ.isPending}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(row) => referensiOptionLabel(row)}
            renderInput={(p) => <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />}
          />
        );
      }
      if (f.relation === 'user_type') {
        const opts = userTypesQ.data ?? [];
        const val =
          opts.find((row) => row.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={userTypesQ.isPending}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(row) =>
              [row.type_name, row.type_code].filter(Boolean).join(' · ') || String(row.documentId ?? '')
            }
            renderInput={(p) => (
              <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />
            )}
          />
        );
      }
      if (f.relation === 'analis_type_data') {
        const opts = analisTypeDataQ.data ?? [];
        const val = opts.find((row) => row.documentId === String(form[f.attribute] ?? '')) ?? null;
        return (
          <Autocomplete
            options={opts}
            value={val}
            loading={analisTypeDataQ.isPending}
            onChange={(_, v) => setForm((p) => ({ ...p, [f.attribute]: v?.documentId ?? '' }))}
            getOptionLabel={(row) => String(row.title ?? '').trim() || String(row.documentId ?? '')}
            renderInput={(p) => (
              <TextField {...p} label={f.label} size="small" required={Boolean(f.required)} />
            )}
          />
        );
      }
      return null;
    },
    [
      customersQ.data?.rows,
      def,
      envVarQ.data,
      envVarQ.isPending,
      form,
      labBakuMutuQ.data,
      labBakuMutuQ.isPending,
      labGroupingQ.data,
      labGroupingQ.isPending,
      labJenisQ.data,
      labJenisQ.isPending,
      labParameterUjiQ.data,
      labParameterUjiQ.isPending,
      labSniMethodQ.data,
      labSniMethodQ.isPending,
      labReferensiQ.data,
      labReferensiQ.isPending,
      quotationsQ.data?.rows,
      samplesQ.data?.rows,
      samplesPointQ.data,
      samplesPointQ.isPending,
      testResultsQ.data?.rows,
      userTypesQ.data,
      userTypesQ.isPending,
      analisTypeDataQ.data,
      analisTypeDataQ.isPending,
      usersQ.data?.rows,
    ],
  );

  const renderRelationManyField = useCallback(
    (f: Extract<MasterFormField, { kind: 'relation_many' }>) => {
      if (f.payload_field === 'jenis_pengukuran') {
        const opts = labJenisQ.data ?? [];
        const selectedIds = Array.isArray(form[f.attribute])
          ? (form[f.attribute] as string[]).map((id) => String(id ?? '').trim()).filter(Boolean)
          : [];
        const value = opts.filter((row) => selectedIds.includes(row.documentId));
        return (
          <Autocomplete
            multiple
            options={opts}
            value={value}
            loading={labJenisQ.isPending}
            onChange={(_, rows) =>
              setForm((p) => ({ ...p, [f.attribute]: rows.map((row) => row.documentId) }))
            }
            getOptionLabel={(row) => jenisPengukuranOptionLabel(row)}
            isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
            renderInput={(params) => (
              <TextField
                {...params}
                label={f.label}
                size="small"
                fullWidth
                required={Boolean(f.required) && value.length === 0}
                inputProps={{
                  ...params.inputProps,
                  required: Boolean(f.required) && value.length === 0,
                }}
              />
            )}
          />
        );
      }
      if (f.payload_field === 'form_question') {
        const selectedIds = Array.isArray(form[f.attribute])
          ? (form[f.attribute] as string[]).map((id) => String(id ?? '').trim()).filter(Boolean)
          : [];
        return (
          <FormQuestionGroupedCheckboxField
            key={editingId ?? 'create'}
            label={f.label}
            questions={labFormQuestionQ.data ?? []}
            selectedIds={selectedIds}
            loading={labFormQuestionQ.isPending}
            required={Boolean(f.required)}
            onChange={(nextIds) => setForm((p) => ({ ...p, [f.attribute]: nextIds }))}
          />
        );
      }
      const kategori = f.kode_kategori;
      if (!kategori) return null;
      const opts = referensiOptionsByKategori[kategori] ?? [];
      const selectedIds = Array.isArray(form[f.attribute])
        ? (form[f.attribute] as string[]).map((id) => String(id ?? '').trim()).filter(Boolean)
        : [];
      const value = opts.filter((row) => selectedIds.includes(row.documentId));
      const loading =
        peralatanPengambilanContohQ.isPending ||
        peralatanPengukuranLapanganQ.isPending ||
        peralatanPendukungQ.isPending ||
        peralatanK3Q.isPending;
      return (
        <Autocomplete
          multiple
          options={opts}
          value={value}
          loading={loading}
          onChange={(_, rows) =>
            setForm((p) => ({ ...p, [f.attribute]: rows.map((row) => row.documentId) }))
          }
          getOptionLabel={(row) => referensiOptionLabel(row)}
          isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
          renderInput={(params) => <TextField {...params} label={f.label} size="small" fullWidth />}
        />
      );
    },
    [
      editingId,
      form,
      labFormQuestionQ.data,
      labFormQuestionQ.isPending,
      labJenisQ.data,
      labJenisQ.isPending,
      peralatanK3Q.isPending,
      peralatanPendukungQ.isPending,
      peralatanPengambilanContohQ.isPending,
      peralatanPengukuranLapanganQ.isPending,
      referensiOptionsByKategori,
    ],
  );

  function renderScalarField(ff: Exclude<MasterFormField, { kind: 'relation' } | { kind: 'relation_many' }>) {
    if (ff.kind === 'boolean') {
      return (
        <FormControlLabel
          key={ff.attribute}
          control={
            <Checkbox
              checked={Boolean(form[ff.attribute])}
              onChange={(ev) => setForm((p) => ({ ...p, [ff.attribute]: ev.target.checked }))}
            />
          }
          label={ff.label}
        />
      );
    }
    if (ff.kind === 'enumeration') {
      return (
        <TextField
          key={ff.attribute}
          select
          label={ff.label}
          required={Boolean(ff.required)}
          size="small"
          fullWidth
          value={String(form[ff.attribute] ?? '')}
          onChange={(ev) => setForm((p) => ({ ...p, [ff.attribute]: ev.target.value }))}
        >
          {ff.options.map((o) => (
            <MenuItem key={o === '' ? '__empty__' : o} value={o}>
              {o === '' ? '— tidak dipilih —' : o}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (ff.kind === 'json') {
      return (
        <TextField
          key={ff.attribute}
          label={ff.label}
          helperText={ff.kind === 'json' && 'helperText' in ff ? ff.helperText : undefined}
          required={Boolean(ff.required)}
          multiline
          minRows={5}
          size="small"
          fullWidth
          value={String(form[ff.attribute] ?? '')}
          onChange={(ev) => setForm((p) => ({ ...p, [ff.attribute]: ev.target.value }))}
        />
      );
    }
    const multiline = Boolean('multiline' in ff && ff.multiline);
    const draft = String(form[ff.attribute] ?? '');
    return (
      <Stack key={ff.attribute} spacing={0.75}>
        <TextField
          label={ff.label}
          type={ff.kind === 'number' ? 'number' : ff.kind === 'email' ? 'email' : 'text'}
          required={Boolean('required' in ff && ff.required)}
          helperText={'helperText' in ff ? ff.helperText : undefined}
          InputProps={
            ff.kind === 'number' && 'step' in ff && ff.step ? ({ inputProps: { step: ff.step } } as const) : undefined
          }
          multiline={multiline}
          minRows={multiline ? 4 : undefined}
          size="small"
          fullWidth
          value={draft}
          onChange={(ev) =>
            setForm((p) => ({
              ...p,
              [ff.attribute]: ff.kind === 'number' ? ev.target.value : ev.target.value,
            }))
          }
        />
        {isMasterChemDisplayField(ff.attribute) && shouldRenderAsChemLatex(draft) ? (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Pratinjau format (LaTeX)
            </Typography>
            <Box
              sx={(theme) => ({
                typography: 'body2',
                p: 1,
                borderRadius: 1,
                bgcolor:
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50],
              })}
            >
              <ChemInline text={draft} />
            </Box>
          </Box>
        ) : null}
      </Stack>
    );
  }

  if (!slug || !def) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">Master data tidak ditemukan</Typography>
        <Typography variant="body2" color="text.secondary">
          Jalur salah atau entitas tidak terdaftar. Gunakan tautan dari menu kiri (Master data).
        </Typography>
      </Box>
    );
  }

  return (
    <PageShell
      title={def.title}
      subtitle={def.description}
      actions={
        <Tooltip title={`Tambah baris baru — ${def.apiPlural}`}>
          <IconButton
            color="primary"
            onClick={openCreate}
            aria-label="Add row"
            size="small"
            sx={{ bgcolor: 'action.hover' }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>

        {slug === 'form-questions' ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            useFlexGap
            sx={{ mb: 2, alignItems: { md: 'flex-end' } }}
          >
            <Autocomplete
              freeSolo
              options={formQuestionNameFilterOptions}
              value={fqNamaFormFilter}
              onChange={(_, v) => setFqNamaFormFilter(typeof v === 'string' ? v : v ?? '')}
              onInputChange={(_, v) => setFqNamaFormFilter(v)}
              sx={{ minWidth: { md: 240 }, flex: { md: '1 1 0' } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Filter nama form"
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': 'form-question-filter-name',
                  }}
                />
              )}
            />
            <Autocomplete
              multiple
              options={labJenisQ.data ?? []}
              value={(labJenisQ.data ?? []).filter((row) => fqJenisFilterIds.includes(row.documentId))}
              loading={labJenisQ.isPending}
              onChange={(_, rows) => setFqJenisFilterIds(rows.map((row) => row.documentId))}
              getOptionLabel={(row) => jenisPengukuranOptionLabel(row)}
              isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
              sx={{ minWidth: { md: 280 }, flex: { md: '1.4 1 0' } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Filter jenis pengukuran"
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': 'form-question-filter-jenis',
                  }}
                />
              )}
            />
            <Button
              type="button"
              size="small"
              variant="outlined"
              disabled={!fqNamaFormFilter && fqJenisFilterIds.length === 0}
              onClick={() => {
                setFqNamaFormFilter('');
                setFqJenisFilterIds([]);
              }}
              data-testid="form-question-filter-clear"
            >
              Hapus filter
            </Button>
          </Stack>
        ) : null}

        {slug === 'form-questions' ? (
          <FormQuestionMapJenisBar
            jwt={jwt}
            slug={slug}
            selectedDocumentIds={fqSelectedIds}
            jenisOptions={labJenisQ.data ?? []}
            jenisLoading={labJenisQ.isPending}
            onMapped={() => setFqSelection([])}
          />
        ) : null}

        {slug === 'form-question-options' ? (
          <FormQuestionOptionMapQuestionsBar
            jwt={jwt}
            slug={slug}
            selectedDocumentIds={fqoSelectedIds}
            questionOptions={labFormQuestionQ.data ?? []}
            questionLoading={labFormQuestionQ.isPending}
            onMapped={() => setFqoSelection([])}
          />
        ) : null}

        {slug === 'baku-mutu-parameter-ujis' ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            useFlexGap
            sx={{ mb: 2, alignItems: { md: 'flex-end' } }}
          >
            <TextField
              size="small"
              label="Filter nama parameter"
              value={bmpuNamaParameterFilter}
              onChange={(e) => setBmpuNamaParameterFilter(e.target.value)}
              sx={{ minWidth: { md: 200 }, flex: { md: '1 1 0' } }}
              inputProps={{ 'data-testid': 'bmpu-filter-nama-parameter' }}
            />
            <TextField
              size="small"
              label="Filter nama baku mutu"
              value={bmpuNamaBakuMutuFilter}
              onChange={(e) => setBmpuNamaBakuMutuFilter(e.target.value)}
              sx={{ minWidth: { md: 200 }, flex: { md: '1 1 0' } }}
              inputProps={{ 'data-testid': 'bmpu-filter-nama-baku-mutu' }}
            />
            <TextField
              size="small"
              label="Filter judul baku mutu"
              value={bmpuJudulBakuMutuFilter}
              onChange={(e) => setBmpuJudulBakuMutuFilter(e.target.value)}
              sx={{ minWidth: { md: 200 }, flex: { md: '1 1 0' } }}
              inputProps={{ 'data-testid': 'bmpu-filter-judul-baku-mutu' }}
            />
            <Button
              type="button"
              size="small"
              variant="outlined"
              disabled={!bmpuNamaParameterFilter && !bmpuNamaBakuMutuFilter && !bmpuJudulBakuMutuFilter}
              onClick={() => {
                setBmpuNamaParameterFilter('');
                setBmpuNamaBakuMutuFilter('');
                setBmpuJudulBakuMutuFilter('');
              }}
              data-testid="bmpu-filter-clear"
            >
              Hapus filter
            </Button>
          </Stack>
        ) : null}

        {slug === 'parameter-uji-metodas' ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            useFlexGap
            sx={{ mb: 2, alignItems: { md: 'flex-end' } }}
          >
            <TextField
              size="small"
              label="Filter parameter uji"
              placeholder="Cari nama atau simbol…"
              value={pumNamaParameterFilter}
              onChange={(e) => setPumNamaParameterFilter(e.target.value)}
              sx={{ minWidth: { md: 260 }, flex: { md: '1 1 0' } }}
              inputProps={{ 'data-testid': 'pum-filter-nama-parameter' }}
            />
            <Button
              type="button"
              size="small"
              variant="outlined"
              disabled={!pumNamaParameterFilter}
              onClick={() => setPumNamaParameterFilter('')}
              data-testid="pum-filter-clear"
            >
              Hapus filter
            </Button>
          </Stack>
        ) : null}

        {slug === 'parameter-ujis' ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            useFlexGap
            sx={{ mb: 2, alignItems: { md: 'flex-end' } }}
          >
            <TextField
              size="small"
              label="Filter parameter"
              placeholder="Cari nama atau simbol…"
              value={puNamaParameterFilter}
              onChange={(e) => setPuNamaParameterFilter(e.target.value)}
              sx={{ minWidth: { md: 260 }, flex: { md: '1 1 0' } }}
              inputProps={{ 'data-testid': 'pu-filter-nama-parameter' }}
            />
            <Button
              type="button"
              size="small"
              variant="outlined"
              disabled={!puNamaParameterFilter}
              onClick={() => setPuNamaParameterFilter('')}
              data-testid="pu-filter-clear"
            >
              Hapus filter
            </Button>
          </Stack>
        ) : null}

        {slug === 'baku-mutus' ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            useFlexGap
            sx={{ mb: 2, alignItems: { md: 'flex-end' } }}
          >
            <TextField
              size="small"
              label="Filter kode"
              value={bakuKodeFilter}
              onChange={(e) => setBakuKodeFilter(e.target.value)}
              sx={{ minWidth: { md: 160 }, flex: { md: '1 1 0' } }}
            />
            <TextField
              size="small"
              label="Filter nama"
              value={bakuNamaFilter}
              onChange={(e) => setBakuNamaFilter(e.target.value)}
              sx={{ minWidth: { md: 180 }, flex: { md: '1 1 0' } }}
            />
            <TextField
              size="small"
              label="Filter judul"
              value={bakuJudulFilter}
              onChange={(e) => setBakuJudulFilter(e.target.value)}
              sx={{ minWidth: { md: 180 }, flex: { md: '1 1 0' } }}
            />
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => {
                setBakuKodeFilter('');
                setBakuNamaFilter('');
                setBakuJudulFilter('');
              }}
            >
              Hapus filter
            </Button>
          </Stack>
        ) : null}

        {slug === 'referensis' ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            useFlexGap
            sx={{ mb: 2, alignItems: { md: 'flex-end' } }}
          >
            <TextField
              size="small"
              label="Filter kode kategori"
              value={refKodeKategoriFilter}
              onChange={(e) => setRefKodeKategoriFilter(e.target.value)}
              sx={{ minWidth: { md: 160 }, flex: { md: '1 1 0' } }}
            />
            <TextField
              size="small"
              label="Filter kode"
              value={refKodeFilter}
              onChange={(e) => setRefKodeFilter(e.target.value)}
              sx={{ minWidth: { md: 140 }, flex: { md: '1 1 0' } }}
            />
            <TextField
              size="small"
              label="Filter parent"
              value={refParentFilter}
              onChange={(e) => setRefParentFilter(e.target.value)}
              helperText="Kode / kategori / deskripsi induk"
              sx={{ minWidth: { md: 180 }, flex: { md: '1 1 0' } }}
            />
            <TextField
              size="small"
              label="Filter status"
              value={refStatusFilter}
              onChange={(e) => setRefStatusFilter(e.target.value)}
              sx={{ minWidth: { md: 120 }, flex: { md: '0 1 120px' } }}
            />
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => {
                setRefKodeKategoriFilter('');
                setRefKodeFilter('');
                setRefParentFilter('');
                setRefStatusFilter('');
              }}
            >
              Hapus filter
            </Button>
          </Stack>
        ) : null}

        {slug === 'jenis-pengukurans' ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            useFlexGap
            sx={{ mb: 2, alignItems: { md: 'flex-end' } }}
          >
            <TextField
              size="small"
              label="Filter grouping id"
              value={jpGroupingIdFilter}
              onChange={(e) => setJpGroupingIdFilter(e.target.value)}
              sx={{ minWidth: { md: 200 }, flex: { md: '1 1 0' } }}
            />
            <TextField
              size="small"
              label="Filter nama"
              value={jpNamaFilter}
              onChange={(e) => setJpNamaFilter(e.target.value)}
              sx={{ minWidth: { md: 180 }, flex: { md: '1 1 0' } }}
            />
            <TextField
              size="small"
              label="Filter kode"
              value={jpKodeFilter}
              onChange={(e) => setJpKodeFilter(e.target.value)}
              sx={{ minWidth: { md: 140 }, flex: { md: '1 1 0' } }}
            />
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => {
                setJpGroupingIdFilter('');
                setJpNamaFilter('');
                setJpKodeFilter('');
              }}
            >
              Hapus filter
            </Button>
          </Stack>
        ) : null}

        {!listQ.isPending && !listQ.isError && rows.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {hasActiveListFilters
              ? 'Tidak ada baris yang cocok dengan filter. Ubah kata kunci atau hapus filter.'
              : 'Tidak ada data. Tambahkan satu baris dengan ikon tambah atau lewat Admin Strapi.'}
          </Alert>
        )}

        {listQ.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {listQ.error instanceof Error ? listQ.error.message : 'Gagal memuat data'}
          </Alert>
        )}

        <DataGrid
          rows={rows}
          columns={columns}
          loading={listQ.isPending}
          getRowId={(r) => r.documentId}
          disableRowSelectionOnClick
          checkboxSelection={slug === 'form-questions' || slug === 'form-question-options'}
          keepNonExistentRowsSelected={slug === 'form-questions' || slug === 'form-question-options'}
          rowSelectionModel={
            slug === 'form-questions' ? fqSelection : slug === 'form-question-options' ? fqoSelection : []
          }
          onRowSelectionModelChange={(model) => {
            if (slug === 'form-questions') setFqSelection(model);
            if (slug === 'form-question-options') setFqoSelection(model);
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={(m) => setPaginationModel(m)}
          pageSizeOptions={[...DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS]}
          slots={{ pagination: DataGridNumberedPagination }}
          autoHeight
          density="compact"
          getRowHeight={
            slug === 'form-questions' || slug === 'form-question-options' ? () => 'auto' : undefined
          }
          sx={{
            '& .MuiDataGrid-columnHeaders': { fontWeight: 600 },
            ...(slug === 'form-questions' || slug === 'form-question-options'
              ? {
                  '& .MuiDataGrid-cell': {
                    py: 0.75,
                    alignItems: 'flex-start',
                    whiteSpace: 'normal',
                    lineHeight: 'unset',
                  },
                }
              : {}),
          }}
        />
      </Box>

      <Dialog
        open={editorOpen}
        onClose={() => {
          if (!fqSaving) closeEditor();
        }}
        fullWidth
        maxWidth={
          slug === 'form-questions' || slug === 'form-question-options'
            ? 'xl'
            : slug === 'peralatan-to-parameter-ujis'
              ? 'lg'
              : 'md'
        }
        aria-labelledby="master-crud-editor"
      >
        <DialogTitle id="master-crud-editor">
          {slug === 'form-questions'
            ? editorMode === 'create'
              ? 'New — Form sampling'
              : `Edit — ${fqDraft?.formName || 'Form sampling'}`
            : editorMode === 'create'
              ? `New — ${def.title}`
              : `Edit — ${def.title}`}
        </DialogTitle>
        <Box
          component="form"
          onSubmit={submitEditor}
          onKeyDown={(ev) => {
            if (slug !== 'form-questions' && slug !== 'peralatan-to-parameter-ujis') return;
            if (ev.key !== 'Enter') return;
            const target = ev.target as HTMLElement | null;
            if (target?.closest('.MuiAutocomplete-root')) return;
            if (target?.tagName === 'BUTTON') return;
            ev.preventDefault();
          }}
        >
          <DialogContent>
            <Stack spacing={2}>
              {slug === 'jenis-pengukuran-perlengkapans' ? (
                <Alert severity="info">
                  Perlengkapan default (dokumen, peralatan, dll.) disimpan di master ini dan dipakai ulang
                  otomatis saat membuat work order dengan jenis pengukuran yang sama.
                </Alert>
              ) : null}
              {slug === 'peralatan-to-parameter-ujis' ? (
                <Alert severity="info">
                  Centang section checklist kesiapan alat untuk parameter uji ini. Form checklist yang masih
                  kosong akan tercentang otomatis dari mapping yang disimpan.
                </Alert>
              ) : null}
              {slug === 'form-questions' && fqDraft ? (
                <FormQuestionGroupEditor
                  draft={fqDraft}
                  onChange={setFqDraft}
                  jenisOptions={labJenisQ.data ?? []}
                  jenisLoading={labJenisQ.isPending}
                />
              ) : slug === 'peralatan-to-parameter-ujis' && ptpDraft ? (
                <PeralatanToParameterUjiEditor jwt={jwt} draft={ptpDraft} onChange={setPtpDraft} />
              ) : (
                def.formFields.map((ff) =>
                  isMasterFormFieldVisible(ff, form) ? (
                    <Box key={ff.attribute} data-testid={`master-field-${ff.attribute}`}>
                      {ff.kind === 'relation'
                        ? renderRelationField(ff)
                        : ff.kind === 'relation_many'
                          ? renderRelationManyField(ff)
                          : renderScalarField(ff)}
                    </Box>
                  ) : null,
                )
              )}
              {formError ? (
                <Typography variant="body2" color="error">
                  {formError}
                </Typography>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button type="button" onClick={closeEditor} disabled={fqSaving}>
              Batal
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMut.isPending || updateMut.isPending || fqSaving}
            >
              {editorMode === 'create'
                ? createMut.isPending || fqSaving
                  ? 'Membuat…'
                  : 'Buat'
                : updateMut.isPending || fqSaving
                  ? 'Menyimpan…'
                  : 'Simpan'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(pendingDelete) || Boolean(pendingDeleteGroup)}
        onClose={() => {
          if (deleteMut.isPending) return;
          setPendingDelete(null);
          setPendingDeleteGroup(null);
        }}
        maxWidth="xs"
      >
        <DialogTitle>{pendingDeleteGroup ? 'Hapus form?' : 'Hapus baris?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {pendingDeleteGroup
              ? `${pendingDeleteGroup.name} (${pendingDeleteGroup.questions.length} pertanyaan)`
              : pendingDelete?.documentId}
          </Typography>
          {deleteMut.isError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {deleteMut.error instanceof Error ? deleteMut.error.message : 'Gagal menghapus'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (deleteMut.isPending) return;
              setPendingDelete(null);
              setPendingDeleteGroup(null);
            }}
            disabled={deleteMut.isPending}
          >
            Batal
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleteMut.isPending}>
            {deleteMut.isPending ? 'Menghapus…' : 'Hapus'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
